import { Injectable, signal } from '@angular/core';
import { PropertyListModel } from '../models/property-list.model';
import {
  ActivatedRoute,
  Router,
  UrlSerializer,
  UrlTree,
} from '@angular/router';
import { BoundCoordinates } from '../models/map.model';
import { SearchCriteriaModel } from '../models/search-criteria.model';
import { LocationStrategy, Location } from '@angular/common';
import { PropertyMapCluster } from '../models/property-map-cluster.model';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  public clientSearchCriteria = signal<SearchCriteriaModel>(
    this.defaultSearchCriteria(),
  );

  public updateMapProperties = signal<boolean>(false);

  public selectedProperty = signal<PropertyListModel[] | null>(null);

  public listProperties = signal<PropertyListModel[] | null>(null);
  public mapProperties = signal<PropertyMapCluster[] | null>(null);

  public updatePropertyList = signal<number>(0);

  public mapFullWidth = signal<boolean>(false);

  queryParams = new UrlTree();

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private location: Location,
    private locationStrategy: LocationStrategy,
    private serializer: UrlSerializer,
  ) {}

  defaultSearchCriteria() {
    let searchCriterias: SearchCriteriaModel = new SearchCriteriaModel();

    const rating = this.getQueryParam('r');
    if (rating) {
      searchCriterias.RatingMin = +rating;
      searchCriterias.ShowLocationInformation = false;
    }

    const ne = this.getQueryParam('ne');
    const sw = this.getQueryParam('sw');

    if (ne && sw) {
      searchCriterias.Bounderies = new BoundCoordinates();
      searchCriterias.Bounderies.NorthEast = {
        Latitude: +ne.split(',')[0],
        Longitude: +ne.split(',')[1],
      };
      searchCriterias.Bounderies.SouthWest = {
        Latitude: +sw.split(',')[0],
        Longitude: +sw.split(',')[1],
      };
      searchCriterias.ShowLocationInformation = false;
    }

    const searchFilters = this.getQueryParam('s');
    if (searchFilters) {
      const searchFilterIds = searchFilters.split(',');
      searchCriterias.SearchFilter = searchFilterIds.map((x) => +x);

      searchCriterias.ShowLocationInformation = false;
    }

    const sort = this.getQueryParam('sort');
    if (sort) {
      searchCriterias.SortProperty = +sort;
      searchCriterias.ShowLocationInformation = false;
    }

    return searchCriterias;
  }

  getQueryParam(param: string) {
    let value;
    this.activatedRoute?.queryParams.subscribe((params: any) => {
      value = params[param];
    });
    if (!value) {
      return '';
    }

    return value;
  }

  setClientSearchCriteria(
    input: SearchCriteriaModel,
    from: string,
    navigateToUrl?: string,
  ) {
    this.setOrUpdateQueryParams(input, navigateToUrl);

    this.clientSearchCriteria.set({ ...input });
  }

  updateSearchFilters(searchFilters: number[]) {
    let currentValue = this.clientSearchCriteria();
    currentValue.SearchFilter = searchFilters;

    this.setClientSearchCriteria(currentValue, 'updateSearchFilters');
  }

  setBounderies(bounderies: BoundCoordinates) {
    let currentValue = this.clientSearchCriteria();
    currentValue.Bounderies = bounderies;

    this.clientSearchCriteria.set(currentValue);
  }

  setOrUpdateQueryParams(
    searchCriteria: SearchCriteriaModel,
    navigateToUrl?: string,
  ) {
    this.queryParams = this.router.parseUrl(this.router.url);

    this.queryParams.queryParams = {};

    if (searchCriteria.RatingMin && searchCriteria.RatingMin > 1) {
      this.addOrUpdateQueryParams('r', searchCriteria.RatingMin.toString());
    }

    if (
      searchCriteria.Bounderies &&
      searchCriteria.Bounderies.NorthEast &&
      searchCriteria.Bounderies.SouthWest &&
      searchCriteria.Bounderies.NorthEast.Longitude != 0
    ) {
      this.addOrUpdateQueryParams(
        'ne',
        searchCriteria.Bounderies.NorthEast.Latitude.toString() +
          ',' +
          searchCriteria.Bounderies.NorthEast.Longitude.toString(),
      );
      this.addOrUpdateQueryParams(
        'sw',
        searchCriteria.Bounderies.SouthWest.Latitude.toString() +
          ',' +
          searchCriteria.Bounderies.SouthWest.Longitude.toString(),
      );
    } else {
      this.removeQueryParams('ne');
      this.removeQueryParams('sw');
    }

    if (searchCriteria.SearchFilter && searchCriteria.SearchFilter.length > 0) {
      if (
        searchCriteria.SearchFilter &&
        searchCriteria.SearchFilter.length > 0
      ) {
        this.addOrUpdateQueryParams('f', searchCriteria.SearchFilter.join(','));
      }
    }

    if (searchCriteria.RatingMin && searchCriteria.RatingMin > 1) {
      this.addOrUpdateQueryParams('r', searchCriteria.RatingMin.toString());
    }

    if (
      searchCriteria.CustomerRatingMin &&
      searchCriteria.CustomerRatingMin > 1
    ) {
      this.addOrUpdateQueryParams(
        'cr',
        searchCriteria.CustomerRatingMin.toString(),
      );
    }

    if (searchCriteria.SortProperty > 0) {
      this.addOrUpdateQueryParams('s', searchCriteria.SortProperty.toString());
    }

    if (navigateToUrl) {
      let url = '';
      const serilizedTree = this.serializer
        .serialize(this.queryParams)
        .substring(1);

      url = navigateToUrl + '?' + serilizedTree.split('?')[1];

      this.router.navigateByUrl(url);
      return;
    }

    this.location.replaceState(
      this.router
        .createUrlTree([this.locationStrategy.path().split('?')[0]], {
          queryParams: this.queryParams.queryParams,
        })
        .toString(),
    );
  }

  removeQueryParams(key: string) {
    this.location.replaceState(
      this.router
        .createUrlTree([this.locationStrategy.path().split('?')[0]], {
          queryParams: { [key]: null },
          queryParamsHandling: 'merge',
        })
        .toString(),
    );
  }

  addOrUpdateQueryParams(key: string, value: string) {
    this.queryParams.queryParams[key] = value;
  }
}
